import { Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { User } from '../../models/user';
import { RoleEnum } from '../../enums/role.enum';
import { RouteEnum } from '../../enums/routes.enum';
import { AuthService } from '../auth/auth.service';
import { isNoEmptyArray, isValidString } from '@pineapplelab/util';
import moment from 'moment';
import { StatusEnum } from '../../enums/status.enum';
import { PagingQueryOptions } from '../../models/pagingQueryOptions ';
import { PagingService } from '../paging/paging.service';
import { environment } from '../../../environments/environment';
import { ApiService } from '../api/api.service';
import { EmbedTokenClaims } from '../../models/embedToken';
import * as Sentry from '@sentry/angular';

@Injectable({
	providedIn: 'root'
})
export class UserService {

	projectId = this.authService.projectId;

	constructor(
		private dbService: DbService,
		private authService: AuthService,
		private pagingService: PagingService,
		private api: ApiService
	) { }

	async getActiveContentCreators(projectId: string): Promise<User[]> {
		const snapshot = await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH)
			.where('role', '==', RoleEnum.CONTENT_CREATOR)
			.where('status', '==', StatusEnum.ACTIVE).get();
		if (snapshot.empty) {
			return null;
		}
		const users = snapshot.docs.map(user => new User(user.data()));
		return users;
	}

	async getAll(projectId: string): Promise<User[]> {
		const snapshot = await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH).where('active', '==', true).get();
		if (snapshot.empty) {
			return null;
		}
		const users = snapshot.docs.map(course => new User(course.data()));
		return users;
	}
	save(model: User, projectId: string): Promise<void> {
		const json = model.toJson();
		return this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH).doc(model.id).set(json, { merge: true });
	}

	async doesUserExist(value: string, fieldId: 'username' | 'email', projectId: string): Promise<boolean> {
		if (!value || !fieldId) {
			return null;
		}

		const res = await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH)
			.where(fieldId, '==', value).get();

		return !!res.size;
	}

	getFirstAvailableRouteByRole(role: RoleEnum): string {
		switch (role) {
			case RoleEnum.ADMIN:
				return "/" + RouteEnum.CATEGORIES;
			case RoleEnum.CONTENT_CREATOR:
				return "/" + RouteEnum.CATEGORIES;
			case RoleEnum.CONTENT_CONSUMER:
				return "/" + RouteEnum.MY_COURSES;
			case RoleEnum.DEVELOPER:
				return "/" + RouteEnum.PERMISSION;
			default:
				return "/" + RouteEnum.MY_COURSES;
		}
	}

	async getByUsername(username: string): Promise<User | null> {
		const snapshot = await this.dbService.firestore
			.collection(Project.PATH)
			.doc(this.projectId)
			.collection(User.PATH)
			.where('username', '==', username)
			.get();
		if (!snapshot.empty) {
			const userDoc = snapshot.docs[0];
			return new User(userDoc.data());
		}
		return null;
	}

	isExpired(user: User): boolean {
		if (!user) {
			return null;
		}

		return moment(new Date(user?.expirationDate?.seconds * 1000)).isBefore(new Date());
	}

	async deactivate(users: User[], projectId: string): Promise<void> {
		if (!isNoEmptyArray(users)) {
			return;
		}

		const usersToDeactivate = users.filter(user =>
			user && this.isExpired(user) && user.status !== StatusEnum.INACTIVE
		);

		if (usersToDeactivate.length === 0) {
			return;
		}

		const promises = usersToDeactivate.map(user => {
			user.status = StatusEnum.INACTIVE;
			return this.dbService.firestore
				.collection(Project.PATH)
				.doc(projectId)
				.collection(User.PATH)
				.doc(user.id)
				.set({ status: StatusEnum.INACTIVE }, { merge: true });
		});

		await Promise.all(promises);
	}

	async get(id: string, projectId: string): Promise<User> {
		const result = await this.dbService.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH).doc(id).get();
		if (!result.exists) {
			return null;
		}
		return new User(result.data());
	}

	getRoute(role: RoleEnum): string {
		const urlAfterLogin = this.authService.getUrlAfterLogin();
		return isValidString(urlAfterLogin) ? urlAfterLogin : this.getFirstAvailableRouteByRole(role);
	}

	async getStudents(clientUsername: string, conditions: PagingQueryOptions, isDeleted: boolean = false): Promise<User[]> {
		if (conditions?.filters && Array.isArray(conditions.filters) && !isNoEmptyArray(conditions.filters)) {
			conditions.filters.push({ isArray: false, operator: 'in', property: 'client', value: [clientUsername] });
		}
		const queryResults = await this.pagingService.execute(conditions);
		if (queryResults.empty) {
			return [];
		}

		return queryResults.docs.filter((user: any) => user.exists).map((user: any) => new User(user.data()));
	}

	async getMe(): Promise<User> {
		return this.get(this.authService.auth.user?.id, environment.projectId);
	}

	async getPhoneNumber(userId: string): Promise<string | null> {
		const url = `/user/phone-number/${userId}`;
		try {
			const response = await this.api.get(url, { showAPIError: false }) as any;
			const phone = response?.phoneNumber;
			const digitsOnly = phone.replace(/\D/g, '');
			return digitsOnly.length > 10 ? phone.replace(/^\+?1?/, '') : phone;
		} catch (error) {
			Sentry.captureException(error);
			return null;
		}
	}

	async validateEmbedToken(token: string): Promise<EmbedTokenClaims> {
		const res = await this.api.get('/user/validate-embed-token', {
			headers: { 'x-embed-token': token }
		});
		return res?.data;
	}
}
